import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getCloudflareVideoDetails } from '../services/api';
import HdVideo from './HdVideo';
import { useLanguage } from '../context/LanguageContext';

/**
 * VideoModal - Displays a video in a modal overlay
 * Supports Cloudflare Stream and direct videos
 *
 * Props:
 * - isOpen: boolean - Whether the modal is open
 * - onClose: function - Callback to close the modal
 * - video: object - Video object with cloudflare_uid, source, title, description
 */
const VideoModal = ({ isOpen, onClose, video }) => {
  const { language } = useLanguage();
  const modalRef = useRef(null);
  const [cloudflareReady, setCloudflareReady] = useState(false);
  const [cloudflareError, setCloudflareError] = useState(null);
  const [checkingCloudflare, setCheckingCloudflare] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(true); // Default to fullscreen

  // Get Cloudflare Stream embed URL
  const getCloudflareEmbedUrl = (uid) => {
    if (!uid) return null;
    return `https://customer-${uid.slice(0, 8)}.cloudflarestream.com/${uid}/iframe`;
  };

  // Check Cloudflare video readiness
  useEffect(() => {
    if (isOpen && video?.source === 'cloudflare' && video?.cloudflare_uid) {
      setCheckingCloudflare(true);
      setCloudflareError(null);
      
      getCloudflareVideoDetails(video.cloudflare_uid)
        .then((details) => {
          setCloudflareReady(details.readyToStream);
          if (!details.readyToStream) {
            setCloudflareError(language === 'en' ? 'Video is still processing. Please try again shortly.' : 'El video aún se está procesando. Por favor inténtalo en breve.');
          }
        })
        .catch((err) => {
          setCloudflareError('Failed to load video details.');
          console.error('Cloudflare video error:', err);
        })
        .finally(() => {
          setCheckingCloudflare(false);
        });
    } else {
      setCloudflareReady(false);
      setCloudflareError(null);
    }
  }, [isOpen, video, language]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  // Determine which player to use
  const isCloudflare = video?.source === 'cloudflare';
  const isPlaceholder = video?.source === 'placeholder';

  return (
    <AnimatePresence>
      {isOpen && video && (
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleBackdropClick}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black"
          data-testid="video-modal-backdrop"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="relative w-full h-full flex flex-col"
            data-testid="video-modal-content"
          >
            {/* Close Button - Fixed top right */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 p-3 bg-black/50 hover:bg-black/70 rounded-full text-white/90 hover:text-white transition-all"
              data-testid="video-modal-close"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Video Container - Full Screen */}
            <div className="flex-1 relative w-full bg-black flex items-center justify-center">
              {isPlaceholder ? (
                // Empty Slot - No video assigned
                <div className="flex flex-col items-center justify-center text-white/60">
                  <div className="w-20 h-20 rounded-full border-2 border-dashed border-white/30 flex items-center justify-center mb-4">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  </div>
                  <p className="text-lg font-medium text-white/50">{language === 'en' ? 'No video assigned yet' : 'Aún no hay video asignado'}</p>
                  <p className="text-sm text-white/30 mt-1">{language === 'en' ? 'This slot is waiting for a video' : 'Este espacio está esperando un video'}</p>
                </div>
              ) : video?.source === 'direct' ? (
                // Direct Video URL Player
                <video
                  src={video.direct_url}
                  className="absolute inset-0 w-full h-full object-contain"
                  controls
                  playsInline
                  preload="metadata"
                  title={video.title}
                  data-testid="video-modal-direct-video"
                />
              ) : isCloudflare ? (
                // Cloudflare Stream Player
                checkingCloudflare ? (
                  <div className="flex items-center justify-center text-white">
                    <Loader2 className="w-8 h-8 animate-spin mr-2" />
                    <span>{language === 'en' ? 'Loading video...' : 'Cargando video...'}</span>
                  </div>
                ) : cloudflareError ? (
                  <div className="flex items-center justify-center text-white">
                    <p>{cloudflareError}</p>
                  </div>
                ) : cloudflareReady ? (
                  <HdVideo
                    uid={video.cloudflare_uid}
                    fill={true}
                    autoplay={true}
                    testId="video-modal-cloudflare-player"
                  />
                ) : (
                  <div className="flex items-center justify-center text-white">
                    <p>{language === 'en' ? 'Video is processing. Please try again later.' : 'El video se está procesando. Por favor inténtalo más tarde.'}</p>
                  </div>
                )
              ) : (
                // No playable video
                <div className="flex items-center justify-center text-white">
                  <p>{language === 'en' ? 'No video available.' : 'No hay video disponible.'}</p>
                </div>
              )}
            </div>

            {/* Video Info - Bottom overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-1" data-testid="video-modal-title">
                {video.title}
              </h3>
              {video.description && (
                <p className="text-gray-300 text-sm md:text-base line-clamp-2" data-testid="video-modal-description">
                  {video.description}
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VideoModal;
